import styled from 'styled-components';
import SportsbookMarketSelectionsFloating from '../../sportsbook-market-selections-floating';
import { EventWithMarketsAndSelections, MarketTypeName, MarketWithSelections } from '../../../sportsbook-types';
import React, { useMemo } from 'react';
import { MARKET_OUTCOME_TYPE_DEFAULT, MarketOutcomeTypeMap, SelectionStatus } from '../../../sportsbook-constants';
import { is1X2Market, isWinnerMarket } from '../../../sportsbook-utils';
import { SortSelectionsBy, sortSelections } from '../../../hooks/use-sorted-selections';
import SportsbookSelectionButton from '../../sportsbook-selection-button';
import { GAME_CARD_BREAKPOINT } from './sportsbook-game-card-base';
import useSelectionIdToTitle from '../hooks/use-selection-id-to-title';
import { SportsbookOddsUpdatingInline } from '../../odds-updating/sportsbook-odds-updating';
import { SelectionDto } from '../../../api/sbapi/sbapi-api-model';

const getMarketTypeName = (market?: MarketWithSelections) => {
	if (!market) {
		return undefined;
	}

	if (isWinnerMarket(market)) {
		return MarketTypeName.NAME_WINNER;
	}

	if (is1X2Market(market)) {
		return MarketTypeName.NAME_1X2;
	}

	return undefined;
};

const StyledSportsbookSelectionButton = styled(SportsbookSelectionButton)`
	border-radius: 0;
`;

const GameCardMarketSelections = styled(SportsbookMarketSelectionsFloating)`
	div:first-of-type > button:first-of-type {
		border-bottom-left-radius: var(--base-radius-medium);
		border-top-left-radius: var(--base-radius-medium);
	}

	div:last-of-type > button:last-of-type {
		border-bottom-right-radius: var(--base-radius-medium);
		border-top-right-radius: var(--base-radius-medium);
	}
`;

const Wrapper = styled.div`
	grid-column: span 2;

	@container (min-width: ${GAME_CARD_BREAKPOINT}px) {
		grid-column: 2;
	}
`;

type Props = {
	market?: EventWithMarketsAndSelections['markets'][0];
	shouldClearSelectionTitle?: boolean;
	tag: string;
	onClickSelection?: (e: React.MouseEvent<HTMLButtonElement>, selection: SelectionDto) => void;
};

const GameCardMarket: React.FC<Props> = ({ market, shouldClearSelectionTitle = false, tag, onClickSelection }) => {
	const marketTypeName = getMarketTypeName(market);
	const outcomeTypeMap = MarketOutcomeTypeMap[marketTypeName || MARKET_OUTCOME_TYPE_DEFAULT];

	// Use fallback in case selectionsTemplate is not avaialable.
	const marketSize = useMemo(() => {
		if (!market) {
			return 1;
		}

		if (market.marketType.selectionsTemplate.length) {
			return market?.marketType.selectionsTemplate.length;
		}

		if (is1X2Market(market)) {
			return 3;
		}

		if (isWinnerMarket(market)) {
			return 2;
		}

		if (market.selections.length > 1) {
			return market.selections.length;
		}

		return 1;
	}, [market]);

	const selectionsTemplate =
		market?.marketType.selectionsTemplate && market.marketType.selectionsTemplate.length > 0
			? market.marketType.selectionsTemplate
			: undefined; // Explicitly set to undefined because the default value for selectionsTemplate is an empty array.

	/** Ideally we can rely on the selectionsTemplate to allow us to map where the selections should be positioned in a grid.
				If selectionsTemplate is not available we fall back to a less dynamic solution in order to sort the selections in correct order */
	const sortedSelections = useMemo(() => {
		if (!market || !market.selections?.length) {
			return [];
		}

		if (selectionsTemplate) {
			// Let the selectionsTemplate define which selections are shown and in which order.
			return market.selections;
		}

		return [...market.selections].sort((s1, s2) => sortSelections(s1, s2, [SortSelectionsBy.OutcomeTypeOrder]));
	}, [selectionsTemplate, market]);

	const selectionIdToTitle = useSelectionIdToTitle(sortedSelections, selectionsTemplate, shouldClearSelectionTitle);

	return (
		<Wrapper>
			{!market || market.selections.length === 0 ? (
				<SportsbookOddsUpdatingInline />
			) : (
				<GameCardMarketSelections
					selections={sortedSelections}
					selectionsTemplate={selectionsTemplate}
					columnCount={marketSize}
					outcomeTypeMap={outcomeTypeMap}
					renderSelection={selection => (
						<StyledSportsbookSelectionButton
							label={selectionIdToTitle[selection.id]}
							selection={selection}
							isDisabled={market.isSuspended || selection.status === SelectionStatus.DISABLED}
							onClick={onClickSelection}
							tag={tag}
						/>
					)}
				/>
			)}
		</Wrapper>
	);
};

export default GameCardMarket;
